import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that displays a product MPN (Manufacturer Part Number).
 * Reads resource.mpn, which theme Liquid snippets embed alongside the
 * standard variant JSON (see variant-main-picker.liquid, variant-swatches.liquid,
 * product-card-rendering.liquid).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} mpnContainer - The container element for the MPN
 * @property {HTMLElement} mpn - The span element that displays the MPN text
 *
 * @extends {Component<Refs>}
 */
class ProductMpnComponent extends Component {
  requiredRefs = ['mpnContainer', 'mpn'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the MPN.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }
        if (resource) {
          const variantMpn = resource.mpn || '';

          if (variantMpn) {
            this.style.display = 'block';
            this.refs.mpn.textContent = variantMpn;
          } else {
            this.style.display = 'none';
            this.refs.mpn.textContent = '';
          }
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-mpn] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-mpn-component')) {
  customElements.define('product-mpn-component', ProductMpnComponent);
}